import { User } from '../../domain/entities/user.entity';
import { Name } from '../../../../shared/domain/value-objects/name.vo';
import { Email } from '../../../../shared/domain/value-objects/email.vo';
import { Phone } from '../../../../shared/domain/value-objects/phone.vo';
import { Dni } from '../../domain/value-objects/dni.vo';
import { AppRole } from '../../../auth/domain/enums/app-role.enum';
import { CreateUserDto } from '../../application/dtos/create-user.dto';
import { UpdateUserDto } from '../../application/dtos/update-user.dto';
import { UpdateUserIdentityDto } from '../../application/dtos/update-user-identity.dto';

export interface UserApiResponse {
  id: string;
  nationalIdentityCard: string;
  firstName: string;
  lastName: string;
  email: string;
  fullName: string;
  phone: string;
  userIdentity_IsActive: boolean | null;
  userIdentity_EmailConfirmed: boolean | null;
  roles: number[] | null;
  clientId: string | null;
  clientName: string | null;
  createdAt: string;
  updatedAt: string | null;
}

export interface UserListPagedApiResponse {
  items: UserApiResponse[];
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalItemsCount: number;
  orderField?: string;
  ascendingOrder: boolean;
}

export class UserMapper {
  static toDomain(raw: UserApiResponse): User {
    return {
      id: raw.id,
      nationalIdentityCard: Dni.create(raw.nationalIdentityCard),
      firstName: Name.create(raw.firstName, 'El nombre'),
      lastName: Name.create(raw.lastName, 'Los apellidos'),
      email: Email.create(raw.email),
      fullName: raw.fullName,
      phone: Phone.create(raw.phone),
      isActive: raw.userIdentity_IsActive,
      emailConfirmed: raw.userIdentity_EmailConfirmed,
      clientId: raw.clientId,
      clientName: raw.clientName,
      roles: (raw.roles ?? []).filter((id) =>
        Object.values(AppRole).includes(id)
      ) as AppRole[],
      createdAt: new Date(raw.createdAt),
      updatedAt: raw.updatedAt ? new Date(raw.updatedAt) : undefined,
    };
  }

  static toDomainList(rawList: UserApiResponse[]): User[] {
    return rawList.map((raw) => this.toDomain(raw));
  }

  // ─── Presentation → API ──────────────────────────────────────────────────────

  static sortFieldToApi(field: string): string {
    const mapping: Record<string, string> = {
      'fullName': 'fullname',
      'nationalIdentityCard': 'nationalidentitycard',
      'email': 'email',
      'phone': 'phone',
      'clientName': 'client',
      'createdAt': 'createdat',
    };
    return mapping[field] ?? field.toLowerCase();
  }

  static dateFilterToIso(value: unknown): string {
    const date = value instanceof Date ? value : new Date(value as string);
    return date.toISOString();
  }

  static toUpdateUserPasswordDto(user: User, password: string): UpdateUserDto {
    return {
      email: user.email.getValue(),
      firstName: user.firstName.getValue(),
      lastName: user.lastName.getValue(),
      nationalIdentityCard: user.nationalIdentityCard.getValue(),
      phone: user.phone.getValue(),
      password,
    };
  }

  static toUpdateUserIdentityDto(isActive: boolean, clientId: string | null, roleId: string): UpdateUserIdentityDto {
    return {
      isActive,
      clientId: clientId === null ? null : clientId || undefined,
      roles: roleId ? [Number(roleId)] : undefined,
    };
  }

  static toCreateUserDto(
    personalData: { firstName: string; lastName: string; nationalIdentityCard: string; email: string; phone: string },
    password: string,
    isActive: boolean,
    sendWelcomeEmail: boolean,
    isTemporalPassword: boolean,
    clientId: string,
    roleId: string,
  ): CreateUserDto {
    return {
      ...personalData,
      password,
      isActive,
      sendWelcomeEmail,
      isTemporalPassword,
      clientId: clientId || undefined,
      roleIds: roleId ? [Number(roleId)] : undefined,
    };
  }
}
